/**
 * Frame20_1 - Calculate Number of Death Certificates Needed
 *
 * Purpose: Help executor determine how many certified copies to order
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Minus, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/Button';
import { SettlementLayout } from '../settlement/SettlementLayout';

export default function Frame20_1() {
  const navigate = useNavigate();
  const { estateId } = useParams();

  const [counts, setCounts] = useState({
    banks: 2,
    investments: 1,
    insurance: 1,
    retirement: 1,
    realEstate: 1,
    vehicles: 1,
    government: 3,
    utilities: 1,
    probateCourt: 2,
    other: 2,
  });

  const totalNeeded = Object.values(counts).reduce((sum, count) => sum + count, 0);

  const updateCount = (key: keyof typeof counts, delta: number) => {
    setCounts((prev) => ({
      ...prev,
      [key]: Math.max(0, prev[key] + delta),
    }));
  };

  const handleNext = () => {
    navigate(estateId ? `/frame-244/${estateId}` : '/frame-244');
  };

  const handleBack = () => {
    navigate(estateId ? `/frame-20/${estateId}` : '/frame-20');
  };

  const categories = [
    { key: 'banks' as const, label: 'Banks & Credit Unions', description: 'One per account' },
    { key: 'investments' as const, label: 'Investment/Brokerage Accounts', description: 'One per firm' },
    { key: 'insurance' as const, label: 'Life Insurance Companies', description: 'One per policy' },
    { key: 'retirement' as const, label: 'Retirement Accounts (401k, IRA)', description: 'One per account' },
    { key: 'realEstate' as const, label: 'Real Estate Transfers', description: 'One per property' },
    { key: 'vehicles' as const, label: 'Vehicle Title Transfers', description: 'One per vehicle' },
    { key: 'government' as const, label: 'Government Agencies (SS, VA, Medicare)', description: 'Usually 2-3 total' },
    { key: 'utilities' as const, label: 'Utilities & Service Providers', description: 'Usually 1 total' },
    { key: 'probateCourt' as const, label: 'Probate Court Filings', description: 'Usually 1-2' },
    { key: 'other' as const, label: 'Other Institutions', description: 'Extra copies for unforeseen needs' },
  ];

  return (
    <SettlementLayout
      currentPhase="iad"
      currentStep="death-certificates"
      estateId={estateId || null}
    >
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Calculate Copies Needed</h1>
          <p className="text-gray-600">
            Estimate how many death certificates you'll need based on estate complexity
          </p>
        </div>

        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-6 mb-6">
          <div className="text-center">
            <div className="text-5xl font-bold text-cyan-900 mb-2">{totalNeeded}</div>
            <div className="text-cyan-700 font-medium">Estimated Copies Needed</div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Adjust Based on Your Estate
          </h2>
          <p className="text-gray-600 mb-6 text-sm">
            Use the + and - buttons to adjust quantities based on the specific assets and accounts
            in this estate.
          </p>
          <div className="space-y-4">
            {categories.map(({ key, label, description }) => (
              <div key={key} className="flex items-center justify-between border-b border-gray-100 pb-4 last:border-b-0">
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">{label}</div>
                  <div className="text-sm text-gray-600">{description}</div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => updateCount(key, -1)}
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                  >
                    <Minus className="w-4 h-4 text-gray-700" />
                  </button>
                  <div className="w-8 text-center font-semibold text-lg">{counts[key]}</div>
                  <button
                    onClick={() => updateCount(key, 1)}
                    className="w-8 h-8 rounded-full bg-cyan-100 hover:bg-cyan-200 flex items-center justify-center"
                  >
                    <Plus className="w-4 h-4 text-cyan-700" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleNext} size="lg">
            Order Certificates
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </SettlementLayout>
  );
}
